import classNames from 'classnames';
import {observer, Provider} from 'mobx-react';
import React, {useCallback, useEffect, useState, useMemo, useRef, useReducer} from 'react';

import {
    AsyncPriceCourseCardContext,
    BrowseCourseBadgesContext,
    COURSE_BADGE_PREMIUM,
} from '@udemy/browse-course';
import {
    PAGE_TYPE_CATEGORY,
    PAGE_TYPE_ORG_CATEGORY,
    PAGE_TYPE_ORG_SUBCATEGORY,
    PAGE_TYPE_SUBCATEGORY,
    PAGE_TYPE_SUBS_CATEGORY,
    PAGE_TYPE_SUBS_SUBCATEGORY,
    DiscoveryUnit,
    DiscoveryUnitItem,
} from '@udemy/discovery-api';
import {Tracker} from '@udemy/event-tracking';
import {useMatchMedia} from '@udemy/hooks';
import {useI18n} from '@udemy/i18n';
import {
    CourseUnitSkeletonVerticalLayout,
    DiscoveryUnitsContainer,
    DiscoveryUnitsLoadingSkeleton,
} from '@udemy/react-discovery-units';
import {ExperimentImpressionEvent, useFeatures} from '@udemy/react-experimentation-provider';
import {SubcategoryLinkBar, SubcategoryLinkBarLoader, CategoryShape} from '@udemy/react-link-bar';
import {useConsumerSubscriptionPlan} from '@udemy/subscription-graphql';
import {UDData, useUDData, useUpdateUDData} from '@udemy/ud-data';

import {
    QUICK_VIEW_BOX_FEATURE,
    USE_STANDARDIZE_MOBILE_BROWSE_CAROUSEL,
    MX_MOBILE_VERTICAL_RECO_UNITS,
    VALUE_PLANSTARTER_PLAN,
} from 'src/lib/experimentation-constants';
import {
    getBrowseCourseBadgesContextOptionsByExperiment,
    SubscriptionCatalogDifferentiatorFeatureType,
} from 'src/lib/experimentation-helper';
import ResourceContextMenuProvider from 'udemy-django-static/js/organization-common/resource-context-menu/resource-context-menu-provider.react-component';

import {fetchCategoryDataFromGql} from '../../pages/courses/lib/get-category-data';
import {parseCategoryData} from '../../pages/courses/lib/parse-category-data';
import styles from './category.module.less';
import {DiscoveryListContainer} from './components/browse/discovery-list-container/discovery-list-container.react-component';
import {RefundNotice} from './components/browse/notices/refund-notice.react-component';
import {
    fetchDiscoveryUnits,
    getDiscoveryUnitComponentAndPropsFallback,
} from './lib/discovery-units-component-and-props';

export interface CategoryProps {
    pageObject: {
        id: number;
        category_id?: number;
        title_cleaned: string;
        title: string;
        type: typeof PAGE_TYPE_CATEGORY | typeof PAGE_TYPE_SUBCATEGORY;
    };
    staticCategory?: CategoryShape;
    subsCollectionIds?: string;
    showCodingExercisesBadge?: boolean;
    staticDiscoveryUnits?: DiscoveryUnit[];
    subscriptionCatalogDifferentiatorFeature?: SubscriptionCatalogDifferentiatorFeatureType;
}

export const CategoryPage: React.FC<CategoryProps> = observer(
    ({
        pageObject,
        staticCategory,
        showCodingExercisesBadge,
        staticDiscoveryUnits = [],
        subsCollectionIds,
        subscriptionCatalogDifferentiatorFeature,
    }) => {
        const {gettext, interpolate, locale} = useI18n();
        const udData = useUDData();
        const {Config: udConfig} = udData;
        const hasOrganization = udConfig.brand.has_organization;
        // Replace multiple loading states with a single reducer
        interface UnitsState {
            units: DiscoveryUnit[];
            isSubscriptionPlanLoading: boolean;
            isDiscoveryUnitsLoaded: boolean;
            isFetchingUnits: boolean;
        }

        type UnitsAction =
            | {type: 'SUBSCRIPTION_PLAN_LOADED'}
            | {type: 'FETCH_UNITS_START'}
            | {type: 'FETCH_UNITS_SUCCESS'; units: DiscoveryUnit[]}
            | {type: 'RENDER_STATIC_UNITS'; units: DiscoveryUnit[]};

        const unitsReducer = (state: UnitsState, action: UnitsAction): UnitsState => {
            switch (action.type) {
                case 'SUBSCRIPTION_PLAN_LOADED':
                    return {...state, isSubscriptionPlanLoading: false};
                case 'FETCH_UNITS_START':
                    return {...state, isFetchingUnits: true};
                case 'FETCH_UNITS_SUCCESS':
                    return {
                        ...state,
                        units: action.units,
                        isDiscoveryUnitsLoaded: true,
                        isFetchingUnits: false,
                    };
                case 'RENDER_STATIC_UNITS':
                    return {
                        ...state,
                        units: action.units,
                        isDiscoveryUnitsLoaded: true,
                    };
                default:
                    return state;
            }
        };

        const initialUnitsState: UnitsState = {
            units: [],
            isSubscriptionPlanLoading: true,
            isDiscoveryUnitsLoaded: false,
            isFetchingUnits: false,
        };

        const [categoryState, setCategoryState] = useState({
            category: staticCategory,
            isLoading: !staticCategory || Object.keys(staticCategory).length === 0,
        });
        const [unitsState, dispatchUnitsAction] = useReducer(unitsReducer, initialUnitsState);
        const {units, isSubscriptionPlanLoading, isDiscoveryUnitsLoaded, isFetchingUnits} =
            unitsState;

        const consumerSubscriptionPlan = useConsumerSubscriptionPlan();

        useEffect(() => {
            // In some cases, a client-side call to retrieve cat/subcat from GQL may succeed where the server-side call failed.
            // For example, the server-side call is not associated with an organization, so if a category is in the UB collection
            // but not in the MX collection, then the server-side call will return nothing, but a client-side call (if associated with
            // an organization) will return something.
            //
            // So, if the server-side category doesn't exist, try to retrieve it client-side.
            const fetchCategoryFromGql = async () => {
                const slug = pageObject.category_id ?? pageObject.title_cleaned;
                fetchCategoryDataFromGql(
                    pageObject.category_id?.toString() ?? pageObject.title_cleaned,
                    locale,
                ).then((categoryData) => {
                    if (categoryData) {
                        setCategoryState({
                            category: parseCategoryData(
                                categoryData,
                                `Unable to parse categoryData with slug: ${slug} in component.`,
                            ),
                            isLoading: false,
                        });
                    } else {
                        setCategoryState({category: undefined, isLoading: false});
                    }
                });
            };

            if (!staticCategory || Object.keys(staticCategory).length === 0) {
                fetchCategoryFromGql();
            }
        }, [staticCategory, pageObject.title_cleaned, pageObject.category_id, locale]);

        useEffect(() => {
            if (hasOrganization) {
                dispatchUnitsAction({type: 'SUBSCRIPTION_PLAN_LOADED'});
            } else if (!consumerSubscriptionPlan?.isLoading) {
                dispatchUnitsAction({type: 'SUBSCRIPTION_PLAN_LOADED'});
            }
        }, [consumerSubscriptionPlan?.isLoading, hasOrganization]);

        const isConsumerSubsSubscriber = !!(
            !consumerSubscriptionPlan?.isLoading && consumerSubscriptionPlan?.isSubscriber
        );

        // Externalize the loading of discovery units outside the DiscoveryUnitsContainer / DiscoveryUnitsContainerStore
        // to enable us to fetch units during ISR for MX users and re-fetch units when the user is a subscriber / UB user
        const ref: React.RefObject<HTMLDivElement> = React.createRef();
        const isMobile = useMatchMedia('mobile-max');
        const deviceType = isMobile ? 'mobile' : 'desktop';
        const {featureVariantAssignments: features} = useFeatures([
            QUICK_VIEW_BOX_FEATURE,
            USE_STANDARDIZE_MOBILE_BROWSE_CAROUSEL,
            MX_MOBILE_VERTICAL_RECO_UNITS,
            VALUE_PLANSTARTER_PLAN,
        ]);
        const isValueStarterPlanEnabled =
            features.get(VALUE_PLANSTARTER_PLAN)?.configuration?.enableValuePlan ?? false;

        const pageType = useMemo(() => {
            const type = pageObject.type;
            if (type === 'category') {
                if (isConsumerSubsSubscriber) {
                    return PAGE_TYPE_SUBS_CATEGORY;
                }
                return hasOrganization ? PAGE_TYPE_ORG_CATEGORY : PAGE_TYPE_CATEGORY;
            } else if (type === 'subcategory') {
                if (isConsumerSubsSubscriber) {
                    return PAGE_TYPE_SUBS_SUBCATEGORY;
                }
                return hasOrganization ? PAGE_TYPE_ORG_SUBCATEGORY : PAGE_TYPE_SUBCATEGORY;
            }
            return undefined;
        }, [pageObject.type, isConsumerSubsSubscriber, hasOrganization]);

        // Determine if we need to fetch dynamic units - only fetch if the user is a subscriber, org user, or no server-side
        // static units were fetched
        const shouldFetchDynamicUnits = useMemo(() => {
            return (
                (hasOrganization || isConsumerSubsSubscriber || !staticDiscoveryUnits.length) &&
                pageType !== undefined
            );
        }, [hasOrganization, isConsumerSubsSubscriber, pageType, staticDiscoveryUnits.length]);

        const fetchUnits = useCallback(async () => {
            if (pageType) {
                try {
                    dispatchUnitsAction({type: 'FETCH_UNITS_START'});
                    const fetchedUnits = await fetchDiscoveryUnits({
                        udData,
                        pageType: pageType,
                        pageObjectId: pageObject.id,
                    });
                    dispatchUnitsAction({type: 'FETCH_UNITS_SUCCESS', units: fetchedUnits.results});
                } catch (error) {
                    // Handle error if needed
                    dispatchUnitsAction({type: 'FETCH_UNITS_SUCCESS', units: []});
                }
            }
        }, [pageObject.id, pageType, udData]);

        useEffect(() => {
            if (
                udData.isGlobalMeContextLoading ||
                consumerSubscriptionPlan?.isLoading ||
                isDiscoveryUnitsLoaded ||
                isFetchingUnits
            ) {
                return;
            }

            // Once subscription status and organization status are resolved, determine whether to re-fetch units
            if (shouldFetchDynamicUnits) {
                // For org users or subscribers, fetch dynamic units
                fetchUnits();
            } else if (!isSubscriptionPlanLoading) {
                // If no static units are provided and we don't need to fetch dynamic units,
                // set an empty array to avoid infinite loading
                dispatchUnitsAction({type: 'RENDER_STATIC_UNITS', units: staticDiscoveryUnits});
            }
        }, [
            consumerSubscriptionPlan?.isLoading,
            fetchUnits,
            shouldFetchDynamicUnits,
            staticDiscoveryUnits,
            isDiscoveryUnitsLoaded,
            isFetchingUnits,
            isSubscriptionPlanLoading,
            udData.isGlobalMeContextLoading,
        ]);

        const updateUDData = useUpdateUDData();
        const updateUDDataRef = useRef(updateUDData);

        useEffect(() => {
            if (pageObject.id) {
                updateUDDataRef.current({
                    pageContext: {
                        entityId: pageObject.id.toString(),
                    },
                } as UDData);
            }
        }, [pageObject.id]);

        const handlePageChange = () => {
            ref.current?.scrollIntoView({behavior: 'smooth', block: 'start'});
        };

        const getUnitComponentAndProps = (unit: Partial<DiscoveryUnit<DiscoveryUnitItem>>) => {
            const allCoursesInUserSubscription = () => {
                if (unit.item_type === 'course') {
                    return unit.items?.every(
                        (item: DiscoveryUnitItem & {is_in_user_subscription?: boolean}) =>
                            item?.is_in_user_subscription,
                    );
                }
                return false;
            };
            const showAddToCartInThreeCardLayout =
                features.get(MX_MOBILE_VERTICAL_RECO_UNITS)?.configuration?.showAddToCartButtons ??
                false;

            const fourCardBrowseCarouselExperimentProps = {
                gridCols: !isMobile ? 4 : undefined,
                renderExperimentalCourseCards: false,
            };

            const singleCourseUnitExperimentProps = {
                allowAddToCartSuccessModal: false,
                showAddToCartInCourseCard: true,
                showQuickViewBox: false,
                disableMobileAddToCartRedirect: true,
                cartButtonTextGoToCart: gettext('In your cart'),
                cartButtonDisableOnGoToCart: true,
            };
            const stackedMobileLayoutExperimentProps = {
                layout: 'singlecol',
                disableInfiniteScroll: true,
                showInstructorDetails: true,
                maxCards: 12,
                maxCardsPerStack: 3,
                ...singleCourseUnitExperimentProps,
                showAddToCartInCourseCard: !allCoursesInUserSubscription,
            };
            const threeCardVerticalLayoutProps = {
                layout: 'singlecol' as const,
                showStackedProductCardLayout: true,
                disableInfiniteScroll: true,
                showAddToCartInCourseCard: showAddToCartInThreeCardLayout,
                addToCartButtonStyle: 'primary' as const,
                addToCartButtonSize: 'xsmall' as const,
                courseCardProps: {
                    layout: 'vertical',
                    showCourseTag: false,
                    showDescription: false,
                    showDetails: false,
                    showRibbonsLast: true,
                    disablePremiumHighlightedRibbon: true,
                },
                showInstructorDetails: true,
                isMobileMax: isMobile ?? undefined,
                showQuickViewBox: false,
                maxCardsPerStack: 3,
                maxCards: 3,
                showDividers: false,
                loadingSkeleton: <CourseUnitSkeletonVerticalLayout />,
            };
            const useStandardizeMobileBrowseCarousel = !!features.get(
                USE_STANDARDIZE_MOBILE_BROWSE_CAROUSEL,
            )?.configuration?.useStandardizeBrowseCarousel;
            const threeCardVerticalLayoutExperimentIsEnabled =
                features.get(MX_MOBILE_VERTICAL_RECO_UNITS)?.configuration?.showVerticalLayout ??
                false;
            const useMultirowLayout = !!isMobile && !useStandardizeMobileBrowseCarousel;
            const useStackedLayout = !!isMobile;
            const showThreeCardVerticalLayout =
                !!isMobile && threeCardVerticalLayoutExperimentIsEnabled;

            const overridesUnitProps = {
                /* eslint-disable @typescript-eslint/naming-convention */
                PopularTopicsUnit: {
                    titleTypography: isMobile ? 'ud-heading-lg' : undefined,
                },
                PopularInstructorsUnit: {
                    alternateHeadline: {
                        title: unit.title,
                        secondaryText: gettext(
                            'These real-world experts are highly rated by learners like you.',
                        ),
                        secondaryTextClass: styles['heading-secondary-subdued'],
                    },
                    showTitle: false,
                },
                CourseUnit: {
                    fullWidth: isMobile,
                    showPager: !isMobile,
                    layout: useMultirowLayout ? 'multirow' : 'singlerow',
                    titleTypography: isMobile ? 'ud-heading-lg' : undefined,
                    ...{...fourCardBrowseCarouselExperimentProps},
                    ...(useStackedLayout && stackedMobileLayoutExperimentProps),
                    ...(showThreeCardVerticalLayout && threeCardVerticalLayoutProps),
                },
                SingleCourseUnit: {
                    titleTypography: isMobile ? 'ud-heading-lg' : undefined,
                    alternateHeadline: {
                        title: unit.title,
                        secondaryText: gettext(
                            'Many learners enjoyed this highly rated course for its engaging content.',
                        ),
                        secondaryTextClass: styles['heading-secondary-subdued'],
                    },
                    showTitle: false,
                    ...(useStackedLayout && singleCourseUnitExperimentProps),
                },
                SkillsHubUnit: {
                    ...{...fourCardBrowseCarouselExperimentProps},
                    showTitle: false,
                    alternateHeadline: {
                        title: gettext('Courses to get you started'),
                        titleClass: isMobile ? 'ud-heading-lg' : 'ud-heading-xl',
                        secondaryText: gettext(
                            'Explore courses from experienced, real-world experts.',
                        ),
                        secondaryTextClass: styles['heading-secondary-subdued'],
                    },
                    className: 'component-margin',
                    ...(useStackedLayout && stackedMobileLayoutExperimentProps),
                    ...(showThreeCardVerticalLayout && threeCardVerticalLayoutProps),
                },
                /* eslint-enable @typescript-eslint/naming-convention */
            };
            return getDiscoveryUnitComponentAndPropsFallback(unit, overridesUnitProps);
        };

        const headerText = interpolate(
            gettext('%(categoryName)s Courses'),
            {categoryName: pageObject.title},
            true,
        );

        const threeCardVerticalLayoutExperimentIsEnabled =
            features.get(MX_MOBILE_VERTICAL_RECO_UNITS)?.configuration?.showVerticalLayout ?? false;

        const showThreeCardVerticalLayout =
            !!isMobile && threeCardVerticalLayoutExperimentIsEnabled;

        const verticalMobileBrowseLayoutExperimentProps = {
            showAddToCartInCourseCard: true,
            disableMobileAddToCartRedirect: true,
            cartButtonTextGoToCart: gettext('In your cart'),
            cartButtonDisableOnGoToCart: true,
            allowAddToCartSuccessModal: false,
        };

        const browseCourseContextProps = {
            browseBadgeHandlerOptions: {
                ...getBrowseCourseBadgesContextOptionsByExperiment(
                    subscriptionCatalogDifferentiatorFeature,
                ),
                ...(showThreeCardVerticalLayout && {
                    numberOfBadgesToShow: 2,
                    priority: {[COURSE_BADGE_PREMIUM]: Number.MAX_SAFE_INTEGER},
                }),
            },
        };

        const qvbFeature = features.get(QUICK_VIEW_BOX_FEATURE);
        const fullQuickViewBoxFeature = qvbFeature?.configuration?.feature ?? false;

        /* The experiment impression event will  trigger when feature is true */
        if (qvbFeature) {
            const {experimentIds = [], isLocalDefault = false} = qvbFeature;
            Tracker.publishEvent(new ExperimentImpressionEvent(experimentIds, isLocalDefault));
        }

        return (
            <>
                {categoryState.isLoading ? (
                    <SubcategoryLinkBarLoader />
                ) : (
                    <SubcategoryLinkBar category={categoryState.category} />
                )}
                <div className="ud-container ud-page-wrapper">
                    <h1
                        className={classNames(
                            isMobile ? 'ud-heading-serif-xl' : 'ud-heading-serif-xxl',
                            styles['heading-primary'],
                        )}
                    >
                        {headerText}
                    </h1>
                    <Provider showCodingExercisesBadge={showCodingExercisesBadge}>
                        <ResourceContextMenuProvider>
                            <BrowseCourseBadgesContext.Provider value={browseCourseContextProps}>
                                {!isDiscoveryUnitsLoaded || isFetchingUnits ? (
                                    <DiscoveryUnitsLoadingSkeleton className="component-margin" />
                                ) : (
                                    <AsyncPriceCourseCardContext.Provider
                                        value={
                                            showThreeCardVerticalLayout
                                                ? {
                                                      useCourseProductCard: true,
                                                      hideCourseDescription: true,
                                                  }
                                                : {}
                                        }
                                    >
                                        <DiscoveryUnitsContainer
                                            className={'component-margin'}
                                            pageType={pageType}
                                            pageObjectId={pageObject.id}
                                            deviceType={deviceType}
                                            itemCount={6}
                                            getUnitComponentAndProps={getUnitComponentAndProps}
                                            units={units}
                                        />
                                    </AsyncPriceCourseCardContext.Provider>
                                )}
                                <div className="component-margin" ref={ref}>
                                    <h2
                                        className={classNames(
                                            isMobile ? 'ud-heading-lg' : 'ud-heading-xl',
                                            styles['heading-secondary'],
                                        )}
                                    >
                                        {interpolate(
                                            gettext('All %(categoryTitle)s courses'),
                                            {categoryTitle: pageObject.title},
                                            true,
                                        )}
                                    </h2>
                                    {!isConsumerSubsSubscriber && (
                                        <RefundNotice className={styles['refund-notice']} />
                                    )}
                                    <DiscoveryListContainer
                                        pageType={pageType}
                                        pageSize={16}
                                        pageObjectId={pageObject.id}
                                        onPageChange={handlePageChange}
                                        subsCollectionIds={subsCollectionIds}
                                        query={pageObject.title}
                                        fullQuickView={fullQuickViewBoxFeature}
                                        isValueStarterPlanEnabled={isValueStarterPlanEnabled}
                                        {...(!!isMobile
                                            ? verticalMobileBrowseLayoutExperimentProps
                                            : {})}
                                    />
                                </div>
                            </BrowseCourseBadgesContext.Provider>
                        </ResourceContextMenuProvider>
                    </Provider>
                </div>
            </>
        );
    },
);
